import { createContext, useContext, useState, useEffect, useCallback, useMemo } from "react";
import api from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = not authenticated
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const response = await api.get("/auth/me");
      setUser(response.data);
    } catch (error) {
      if (error.response?.status !== 401) {
        console.error("Auth check failed:", error.message);
      }
      setUser(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = async (username, password) => {
    const response = await api.post("/auth/login", { username, password });
    const { user: userData, token } = response.data;
    if (token) localStorage.setItem("access_token", token);
    setUser(userData);
    return userData;
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch (error) {
      console.error("Logout request failed:", error.message);
    }
    localStorage.removeItem("access_token");
    setUser(false);
  };

  const updateUser = useCallback((userData) => {
    setUser(userData);
  }, []);

  const hasPermission = useCallback((module) => {
    if (!user) return false;
    if (user.role === "admin") return true;
    const perms = user.permissions || [];
    return perms.includes(module);
  }, [user]);

  const contextValue = useMemo(() => ({
    user, loading, login, logout, updateUser, hasPermission
  }), [user, loading, updateUser, hasPermission]);

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
